import { Menu } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { productsFor } from '@/app/products'
import { useSession } from '@/shared/auth/useSession'
import { useFeatures } from '@/shared/features/useFeatures'
import {
  BrandLockup,
  Button,
  DialogClose,
  DialogRoot,
  DialogTrigger,
  SheetContent,
} from '@/shared/ui'

import { AccountMenu } from './AccountMenu'
import { LanguageSwitcher } from './LanguageSwitcher'
import { NotificationBell } from './NotificationBell'
import { ProductNav, ProductNavVertical } from './ProductNav'
import { ThemeToggle } from './ThemeToggle'

/**
 * The shell header.
 *
 * Two layouts rather than one that wraps. The controls were previously a single
 * unwrapping row 398px wide, which at 375px pushed the account button off the
 * edge and made the whole document scroll sideways — the one thing a layout must
 * never do, and worse in Arabic where the overflow lands on the side the reader
 * starts from.
 *
 * So: from `md` up, the product navigation sits inline. Below it, everything but
 * the brand and the account button moves into a sheet.
 *
 * **Since Phase 10 the inline nav is the *product* axis** — what am I browsing.
 * The *role* axis — what am I acting as — is entirely inside `AccountMenu`
 * (§1.4, roadmap 10.2). Two questions, two controls, never merged.
 *
 * **Brand, 1 October 2026.** Dressed in the "common thread" reference: a taller
 * bar on the warm page ground with a hairline under it (no white slab, no
 * shadow), the thread mark and serif wordmark at the start, and the sign-in as
 * the one wine button. Only the dress changed — every control and the two-axis
 * split above are exactly as they were.
 *
 * **Task 4 (1 October 2026) matched the reference's measurements**: a 94px bar
 * (78px on a phone), the 46×36 mark beside a 36px wordmark, the product nav as
 * plain 14px text centred between the brand and the actions (no icons, no
 * chips — see `productClass`), the language switcher as one plain word, and the
 * sign-in as the wine button with the reference's 22px sides.
 */
export function Header() {
  const { t } = useTranslation('common')
  const [menuOpen, setMenuOpen] = useState(false)
  // Education is a normal product entry when the flag is on and a badged
  // announcement link when it is off — roadmap 10.4. Read from `/config/`, so
  // flipping it needs no rebuild.
  const { features } = useFeatures()
  // ⚠️ `hasRole` is passed so the Ops entry can appear for a platform admin —
  // the one product that is role-gated at the *product* level rather than per
  // item (30 August 2026). Depending on `session.hasRole` rather than on the
  // whole session keeps this from re-memoising on every unrelated session
  // change; the function identity is stable for a given role set.
  const session = useSession()
  const products = useMemo(
    () =>
      productsFor({
        education: features.education,
        payments: features.payments,
        hasRole: session.hasRole,
      }),
    [features.education, features.payments, session.hasRole],
  )

  return (
    <header className="border-b border-line bg-sunken">
      {/* `gap-2` below `sm`: at 320px in Arabic every pixel of the row is
          spoken for (hamburger, lockup, sign-in) — measured, not guessed. */}
      <div className="flex h-[78px] items-center gap-2 px-gutter sm:gap-3 md:h-[94px]">
        <DialogRoot open={menuOpen} onOpenChange={setMenuOpen}>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('nav.openMenu')}
              className="md:hidden"
            >
              <Menu aria-hidden="true" />
            </Button>
          </DialogTrigger>

          <SheetContent
            // The hamburger is at the inline start, so the drawer is too —
            // opening it on the far edge sends the eye across the viewport,
            // and in Arabic it lands where the user was not looking.
            side="start"
            title={t('nav.menu')}
            description={t('nav.menuDescription')}
            closeLabel={t('nav.closeMenu')}
          >
            {/* `DialogClose asChild` around the nav so tapping a destination
                closes the sheet — a menu that stays open over the page it just
                navigated to reads as a broken tap. */}
            <DialogClose asChild>
              <div>
                <ProductNavVertical products={products} />
              </div>
            </DialogClose>

            <div className="mt-6 border-t border-line pt-6">
              <p className="mb-2 text-xs font-medium tracking-caps text-fg-subtle">
                {t('nav.language')}
              </p>
              <LanguageSwitcher />
            </div>

            {/* Under the language, as the brief places it: the two
                preferences that change how the page reads, together. */}
            <div className="mt-6 border-t border-line pt-6">
              <p className="mb-2 text-xs font-medium tracking-caps text-fg-subtle">
                {t('theme.appearance')}
              </p>
              <ThemeToggle className="text-sm text-fg" />
            </div>
          </SheetContent>
        </DialogRoot>

        {/* The lockup is decorative and LTR in both languages (see
            `BrandLockup`); the link's name comes from the locale, so a screen
            reader in Arabic hears "نكست سين", not a Latin logotype spelled out.
            Rounded so the gold focus ring hugs the lockup instead of a box. */}
        <Link to="/" aria-label={t('brand.name')} className="shrink-0 rounded-[5px]">
          <BrandLockup wordmark={t('brand.wordmark')} />
        </Link>

        {/* Centred in the space the brand and the actions leave, as the
            reference's three-part `space-between` bar sits. */}
        <div className="hidden min-w-0 md:flex md:flex-1 md:justify-center">
          <ProductNav products={products} />
        </div>

        {/* `ms-auto` — margin at the inline start, which is the *right* edge in
            Arabic. `ml-auto` would pin this block to the wrong side in RTL. */}
        <div className="ms-auto flex min-w-0 items-center gap-2 md:ms-0 lg:gap-4">
          <div className="hidden md:block">
            <LanguageSwitcher />
          </div>
          {/* ⚠️ Beside the account button on **every** width, unlike the
              language switcher, which folds into the sheet below `md`. A bell
              that disappears on a phone is a bell nobody sees: the badge is the
              only thing on this header that changes on its own, and hiding it
              behind a hamburger means it is never noticed. It renders nothing
              at all for a signed-out visitor, so it costs an anonymous page
              nothing. */}
          <NotificationBell />
          <AccountMenu />
        </div>
      </div>
    </header>
  )
}
